import * as Sentry from '@sentry/react';
import type { AxiosError } from 'axios';

/**
 * Reports the two API failures that silently put a student back on the
 * login page: a 401 after they signed in (the error handler clears the
 * profile) and a profile load the server refused (getProfile swallows the
 * rejection, so the profile stays empty). Neither shows an error or reaches
 * Sentry otherwise.
 *
 * `signedIn` is whether a profile was loaded when the request failed: every
 * anonymous visit starts with a 401 from GET /profile, which is not a loss.
 * Network failures carry no response and are left out.
 */
export function reportAuthLoss(error: AxiosError, signedIn: boolean): void {
  const status = error.response?.status;
  if (status === undefined) return;

  const method = error.config?.method?.toUpperCase() ?? '';
  const url = (error.config?.url ?? '').split('?')[0];

  const message =
    status === 401
      ? signedIn && 'Signed-in student got a 401'
      : method === 'GET' && url === '/profile' && 'Profile failed to load';
  if (!message) return;

  Sentry.captureMessage(message, {
    level: 'warning',
    tags: { status },
    extra: { method, url }
  });
}
